import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play, Quote, ArrowUpRight } from "lucide-react";

// Real testimonials from dotconverse.com — Umender Shah, Country Manager, ZEISS India
const testimonials = [
  {
    id: "S2hd2SmSv8U",
    topic: "On partnering with dotConverse",
    snippet:
      "How dotConverse stays in the room until the deal closes — not just running campaigns, but rebuilding pipeline thinking.",
    accent: "#FFB000",
  },
  {
    id: "QDd9kmYJaJw",
    topic: "On AI-led B2B pipeline",
    snippet:
      "Why an AI-augmented funnel changed how ZEISS India thinks about demand, qualification, and enterprise outreach.",
    accent: "#3BA7D1",
  },
  {
    id: "NOoOTpfjXyE",
    topic: "On execution & outcomes",
    snippet:
      "What it actually feels like to work with a senior, in-house team that owns delivery end to end.",
    accent: "#0FB5A6",
  },
];

const PERSON = {
  name: "Umender Shah",
  role: "Country Manager",
  company: "ZEISS India",
};

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const lead = testimonials[active];

  return (
    <section
      data-testid="testimonials"
      className="section-surface-light py-section relative overflow-hidden"
    >
      <div className="bg-orbs" />
      <div className="container-xl relative">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#86868B] mb-4">
              Customer testimonials
            </div>
            <h2 className="font-display text-[#1D1D1F] text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] font-semibold">
              Hear from our{" "}
              <span className="font-serif italic font-light text-[#86868B]">
                clients.
              </span>
            </h2>
            <p className="mt-5 text-[#515154] text-base md:text-lg leading-relaxed max-w-xl">
              Real conversations with the leaders we work with — recorded, not
              rewritten.
            </p>
          </div>
          <a
            href="/work"
            data-testid="testimonials-see-stories"
            className="text-[#1D1D1F] text-sm font-medium underline underline-offset-4 decoration-[#FFB000] decoration-2 hover:decoration-4 transition-all self-start md:self-end shrink-0"
          >
            See full client stories
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
          {/* Lead video */}
          <motion.div
            key={lead.id}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-8"
            data-testid="testimonial-lead-card"
          >
            <div
              className="relative aspect-video rounded-[28px] md:rounded-[32px] overflow-hidden border border-black/5 bg-[#0A0A0A] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.35)]"
            >
              {playing ? (
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube-nocookie.com/embed/${lead.id}?autoplay=1&rel=0&modestbranding=1`}
                  title={`${PERSON.name} — ${lead.topic}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <>
                  <img
                    src={`https://i.ytimg.com/vi/${lead.id}/maxresdefault.jpg`}
                    alt={`${PERSON.name} testimonial`}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = `https://i.ytimg.com/vi/${lead.id}/hqdefault.jpg`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />

                  {/* ZEISS brand chip */}
                  <div
                    className="absolute top-5 left-5 md:top-6 md:left-6 inline-flex items-center gap-2 backdrop-blur-md border rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-medium text-white"
                    style={{
                      background: `${lead.accent}33`,
                      borderColor: `${lead.accent}77`,
                    }}
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: lead.accent }}
                    />
                    {lead.topic}
                  </div>

                  <button
                    onClick={() => setPlaying(true)}
                    data-testid="testimonial-lead-play"
                    aria-label={`Play ${PERSON.name} testimonial`}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <span className="relative flex items-center justify-center h-20 w-20 md:h-24 md:w-24 rounded-full bg-white/95 text-black shadow-[0_20px_50px_-10px_rgba(0,0,0,0.5)] transition-transform duration-500 hover:scale-110">
                      <span className="absolute inset-0 rounded-full bg-white/40 animate-ping opacity-60" />
                      <Play
                        size={28}
                        strokeWidth={2}
                        className="relative ml-1"
                        fill="currentColor"
                      />
                    </span>
                  </button>

                  {/* Quote tail */}
                  <div className="absolute left-6 right-6 bottom-6 md:left-8 md:bottom-8 md:right-8">
                    <Quote
                      size={22}
                      strokeWidth={2}
                      color={lead.accent}
                      className="mb-3 opacity-90"
                    />
                    <p className="text-white font-display text-lg md:text-2xl lg:text-3xl tracking-tight font-medium leading-snug max-w-3xl">
                      {lead.snippet}
                    </p>
                    <div className="mt-5 flex items-end justify-between gap-4">
                      <div>
                        <div className="font-display text-white text-base md:text-lg font-medium">
                          {PERSON.name}
                        </div>
                        <div className="text-white/65 text-xs md:text-sm">
                          {PERSON.role} · {PERSON.company}
                        </div>
                      </div>
                      <span className="hidden md:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/70">
                        Watch <ArrowUpRight size={12} strokeWidth={2.2} />
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>

          {/* Side queue */}
          <div className="lg:col-span-4 flex flex-col gap-5 md:gap-6">
            {testimonials.map((t, i) => {
              const isActive = i === active;
              return (
                <button
                  key={t.id}
                  onClick={() => {
                    setActive(i);
                    setPlaying(false);
                  }}
                  data-testid={`testimonial-thumb-${i}`}
                  className={`group relative flex-1 min-h-[140px] rounded-[22px] overflow-hidden border text-left transition-all ${
                    isActive
                      ? "border-[#0A0A0A]/30 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.18)]"
                      : "border-black/8 hover:border-black/25"
                  }`}
                >
                  <img
                    src={`https://i.ytimg.com/vi/${t.id}/hqdefault.jpg`}
                    alt={`${PERSON.name} — ${t.topic}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div
                    className="absolute top-3 left-3 inline-flex items-center gap-1.5 backdrop-blur-md border rounded-full px-2.5 py-1 text-[9px] uppercase tracking-[0.22em] font-medium text-white"
                    style={{
                      background: `${t.accent}33`,
                      borderColor: `${t.accent}77`,
                    }}
                  >
                    <span
                      className="h-1 w-1 rounded-full"
                      style={{ background: t.accent }}
                    />
                    {t.topic}
                  </div>
                  <div
                    className={`absolute right-3 top-3 h-9 w-9 rounded-full flex items-center justify-center transition-transform ${
                      isActive ? "scale-100" : "scale-90 group-hover:scale-100"
                    }`}
                    style={{
                      background: "rgba(255,255,255,0.95)",
                      color: "#0A0A0A",
                    }}
                  >
                    <Play size={13} strokeWidth={2} fill="currentColor" className="ml-0.5" />
                  </div>
                  <div className="absolute left-4 right-4 bottom-3">
                    <div className="text-white text-[13px] font-medium leading-snug">
                      {PERSON.name}
                    </div>
                    <div className="text-white/65 text-[11px]">
                      {PERSON.role} · {PERSON.company}
                    </div>
                  </div>
                  {isActive && (
                    <span
                      className="absolute inset-x-0 bottom-0 h-0.5"
                      style={{ background: t.accent }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Trust tail */}
        <div className="mt-10 md:mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-[#86868B]">
          <span className="text-[11px] uppercase tracking-[0.22em]">
            Also trusted by
          </span>
          {["ZEISS", "Heidelberg", "Konica Minolta", "Tata Steel", "SAP", "HP", "Logitech"].map(
            (b) => (
              <span
                key={b}
                className="font-display text-base md:text-lg font-semibold text-[#1D1D1F]/60 tracking-tight"
                style={{ letterSpacing: "-0.02em" }}
              >
                {b}
              </span>
            )
          )}
        </div>
      </div>
    </section>
  );
}
